import { useState, useEffect } from "react";

/**
 * Returns a debounced version of value that only updates after
 * the specified delay (ms) has elapsed without further changes.
 *
 * Use for search inputs to reduce expensive re-filter operations
 * on every keystroke.
 */
export function useDebounce<T>(value: T, delayMs = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debouncedValue;
}

/**
 * Returns true while value is "in flight" (typed but not yet debounced).
 * Use to show a loading/spinner state in the search input.
 */
export function useIsDebouncing(value: string, debouncedValue: string): boolean {
  return value !== debouncedValue;
}
